import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { BoundedTotal, Maybe } from '@xing-com/crate-common-graphql-types';
import { PageHeader as SerpsPageHeader } from '@xing-com/crate-jobs-domain-serps-components-page-header';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';
import { useSearchState } from '@xing-com/crate-jobs-hooks';
import { SearchAlertsSearchAnimatedButton } from '@xing-com/crate-jobs-xinglets';
import { mediaTopBarHidden } from '@xing-com/layout-tokens';
import { scale130, spaceM } from '@xing-com/tokens';
import { Headline } from '@xing-com/typography';

import { LimitedJobCount } from '../../shared';

type HeaderContentProps = {
  jobCount: number;
  boundedTotals?: Maybe<BoundedTotal>;
};

type PluralCountProps = {
  total: number;
  limit?: number;
};

const PluralCount: React.FC<PluralCountProps> = ({ total, limit }) => {
  const otherKeyId =
    total > 1
      ? 'JOBS_SEARCH_HEADLINE.other'
      : 'JOBS_SEARCH_AI_NO_RESULTS_HEADLINE';

  return (
    <FormattedPlural
      value={total}
      one={
        <FormattedMessage
          id="JOBS_SEARCH_HEADLINE.one"
          values={{ variable_2: 1 }}
        />
      }
      other={
        <FormattedMessage
          id={otherKeyId}
          values={{
            variable_2: <LimitedJobCount count={total} limit={limit} />,
          }}
        />
      }
    />
  );
};

const HeaderContent: React.FC<HeaderContentProps> = ({
  boundedTotals,
  jobCount,
}) => {
  const { isEmptySearch } = useSearchState();

  if (isEmptySearch) {
    return (
      <FormattedMessage id="JOBS_SEARCH_AI_RESULTS_HEADLINE_EMPTY_SEARCH" />
    );
  }

  if (boundedTotals && boundedTotals.total > 0) {
    return (
      <PluralCount
        total={boundedTotals.total}
        limit={boundedTotals.bound - 1}
      />
    );
  }

  // Fallback for AI search with no bounded totals available
  if (jobCount < 0) {
    return <FormattedMessage id="JOBS_SEARCH_AI_RESULTS_HEADLINE" />;
  }

  return <PluralCount total={jobCount} />;
};

type ResultsHeaderProps = {
  jobCount: number;
  boundedTotals?: Maybe<BoundedTotal>;
  serps: SerpsRouteParams | null;
  isStickyHeader?: boolean;
};

export const ResultsHeader: React.FC<ResultsHeaderProps> = ({
  jobCount,
  serps,
  isStickyHeader,
  boundedTotals,
}) => {
  const { formatMessage } = useIntl();

  const a11yTitle =
    boundedTotals && boundedTotals.total >= boundedTotals.bound
      ? formatMessage(
          { id: 'JOBS_SEARCH_RESULTS_HEADLINE_MORE_THAN_A11Y' },
          { count: boundedTotals.total - 1 }
        )
      : undefined;

  return (
    <Container>
      <Headline aria-label={a11yTitle} as="h1" noMargin size="xlarge">
        {serps ? (
          <SerpsPageHeader {...serps} jobCount={jobCount} />
        ) : (
          <HeaderContent boundedTotals={boundedTotals} jobCount={jobCount} />
        )}
      </Headline>
      <SearchAlertsSearchAnimatedButton
        isStickyHeader={!!isStickyHeader}
        empty={jobCount === 0}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spaceM};
  /* Fixed height required so title is center aligned with animated SA button */
  height: ${scale130};

  @media ${mediaTopBarHidden} {
    height: auto;
  }
`;
